import gazImg from '../assets/gaz_traceur_detecteur.webp';
import thermoImg from '../assets/thermographie_chauffage_sol.webp';
import inspectionImg from '../assets/inspection_camera_egout.webp';
import fluoresceineImg from '../assets/fluoresceine_uv.webp';
import humidimetreImg from '../assets/humidimetre_mur.webp';

const PARENT = '/recherche-fuite-luxembourg';

export const fuites = [
  {
    path: '/recherche-fuite-canalisation',
    parent: PARENT,
    nav: 'Fuite de canalisation',
    summary: "Canalisation d'eau encastrée dans une dalle, un mur, ou enterrée sous le jardin.",
    title: 'Fuite de canalisation encastrée ou enterrée | Expomis',
    description: "Recherche de fuite sur canalisation d'eau encastrée ou enterrée, sans casse : contrôle de pression, thermographie, acoustique, gaz traceur. Luxembourg et Arlon.",
    eyebrow: 'Types de fuite',
    h1: 'Recherche de fuite sur canalisation encastrée ou enterrée',
    image: gazImg,
    imageAlt: 'Recherche de fuite sous carrelage au gaz traceur, détecteur et sonde de sol',
    intro: [
      "Une canalisation d'eau qui fuit dans une dalle, un mur ou sous le jardin ne se voit pas : on constate seulement ses effets, compteur qui tourne, tache d'humidité, pression qui baisse. Expomis localise ce type de fuite sans ouvrir au hasard, avec la technique adaptée au réseau concerné.",
    ],
    sections: [
      {
        h2: 'Les signes d’une fuite sur canalisation',
        list: [
          'le compteur d’eau tourne alors que tous les robinets et appareils sont fermés ;',
          'une auréole apparaît au plafond, au pied d’un mur ou sur une chape ;',
          'une zone du sol est plus chaude que le reste (fuite d’eau chaude) ;',
          'on entend un écoulement ou un sifflement permanent ;',
          'la pression au robinet a baissé sans raison apparente.',
        ],
      },
      {
        h2: 'D’abord trouver le circuit, ensuite le point de fuite',
        body: [
          "Une maison compte plusieurs circuits : eau froide, eau chaude, parfois une boucle de recirculation, et le chauffage. Avant de chercher où est la fuite, nous établissons sur quel circuit elle se trouve, par un [contrôle de pression](/techniques/mise-en-pression) tronçon par tronçon. Cette étape réduit la zone de recherche et évite les fausses pistes.",
        ],
      },
      {
        h2: 'Comment nous localisons la fuite',
        list: [
          "Canalisation d'eau chaude : la [caméra thermique](/techniques/camera-thermique) fait apparaître le tracé du tuyau et la zone où l'eau chaude se répand.",
          "Canalisation sous pression, eau froide comprise : l'[écoute acoustique](/techniques/detection-acoustique) repère le bruit de l'eau qui s'échappe.",
          "Fuite faible, silencieuse, sous une dalle épaisse ou enterrée : le [gaz traceur](/techniques/gaz-traceur) remonte à la surface à l'aplomb de la fuite.",
        ],
        after: ["Nous croisons souvent deux méthodes pour confirmer l'emplacement avant de le marquer."],
      },
      {
        h2: 'Conduites enterrées',
        body: [
          "La conduite qui alimente le bâtiment, un garage, un abri de jardin ou un robinet extérieur peut courir sur plusieurs dizaines de mètres sous la pelouse ou l'allée. Le gaz traceur et l'écoute acoustique indiquent où creuser, au lieu d'ouvrir une tranchée sur toute la longueur.",
          "Selon l'endroit de la fuite, la réparation peut relever du distributeur ou de la commune plutôt que du propriétaire. La localiser précisément permet aussi de savoir à qui elle incombe.",
        ],
      },
    ],
    faq: [
      {
        q: 'Mon compteur tourne alors que tout est fermé : est-ce forcément une fuite ?',
        a: "Presque toujours, sauf si un appareil consomme sans que vous le sachiez : chasse d'eau qui coule en continu, adoucisseur en régénération, groupe de sécurité du chauffe-eau qui goutte. Vérifiez ces points d'abord ; notre page [surconsommation d'eau](/surconsommation-eau) explique comment.",
      },
      {
        q: "Faut-il couper l'eau pendant l'intervention ?",
        a: "Pour certains tests, oui : le contrôle de pression ou le gaz traceur demandent d'isoler, voire de vider le tronçon testé. La coupure est limitée à la durée du test.",
      },
    ],
    related: ['/techniques/gaz-traceur', '/techniques/detection-acoustique', '/surconsommation-eau'],
  },
  {
    path: '/recherche-fuite-chauffage',
    parent: PARENT,
    nav: 'Fuite de chauffage',
    summary: 'Chaudière qui perd de la pression, chauffage au sol, radiateurs et canalisations de chauffage.',
    title: 'Fuite de chauffage au sol ou de radiateur | Expomis',
    description: 'Votre chaudière perd de la pression ? Recherche de fuite sur chauffage au sol et radiateurs sans casser la chape : test par boucle, thermographie, gaz traceur.',
    eyebrow: 'Types de fuite',
    h1: 'Recherche de fuite sur chauffage et chauffage au sol',
    image: thermoImg,
    imageAlt: 'Fuite de chauffage au sol visible à la caméra thermique',
    intro: [
      "Une chaudière qui perd de la pression et qu'il faut recharger régulièrement est le signe le plus fréquent d'une fuite sur le circuit de chauffage. Dans un chauffage au sol, les tubes sont noyés dans la chape : la fuite ne se voit pas, et ouvrir le sol au hasard coûte cher. Expomis localise la fuite précisément, sans casse.",
    ],
    sections: [
      {
        h2: 'Les signes d’une fuite de chauffage',
        list: [
          'la pression de la chaudière baisse et vous devez remettre de l’eau régulièrement ;',
          'une zone de sol est plus chaude que le reste, ou une tache apparaît au plafond de l’étage inférieur ;',
          'une trace d’humidité au pied d’un mur ou sous un radiateur ;',
          'un revêtement de sol qui se décolle ou un parquet qui gonfle.',
        ],
      },
      {
        h2: 'Avant de chercher la fuite, vérifier qu’il y en a une',
        body: [
          "Une perte de pression ne vient pas toujours d'une fuite dans le sol. Un vase d'expansion dégonflé ou une soupape de sécurité qui décharge peuvent produire le même symptôme. Nous contrôlons ces éléments, puis nous mettons le circuit en pression boucle par boucle, au collecteur, pour identifier celle qui fuit. Voir le [contrôle de pression](/techniques/mise-en-pression).",
        ],
      },
      {
        h2: 'Pourquoi la caméra thermique est adaptée',
        body: [
          "Le chauffage est un circuit chaud. Quand on le met en chauffe, la [caméra thermique](/techniques/camera-thermique) fait apparaître le tracé des tubes sous le revêtement, puis la tache de chaleur qui se forme là où l'eau s'échappe. Si le contraste thermique est insuffisant (revêtement épais, fuite faible), nous utilisons le [gaz traceur](/techniques/gaz-traceur).",
        ],
      },
    ],
    faq: [
      {
        q: 'Ma chaudière perd de la pression : est-ce forcément une fuite ?',
        a: "Pas forcément. Un vase d'expansion dégonflé ou une soupape de sécurité défectueuse donnent le même symptôme. Si ces éléments sont en ordre et que la pression continue de baisser, il y a très probablement une fuite sur le circuit.",
      },
      {
        q: 'Peut-on réparer un chauffage au sol sans refaire tout le sol ?',
        a: "Oui, dans la plupart des cas. Une fois la fuite localisée, on ouvre la chape localement, on répare le tube avec un raccord, puis on referme. C'est tout l'intérêt d'une localisation précise.",
      },
    ],
    related: ['/techniques/camera-thermique', '/techniques/mise-en-pression', '/recherche-fuite-canalisation'],
  },
  {
    path: '/recherche-fuite-evacuation',
    parent: PARENT,
    nav: 'Fuite d’évacuation et d’égout',
    summary: "Évacuations, descentes, raccordements et égouts : fuites, infiltrations et odeurs d'égout.",
    title: "Fuite d'évacuation et d'égout : fumigène, caméra | Expomis",
    description: "Recherche de fuite sur évacuations, descentes et égouts : fluorescéine, test fumigène, caméra d'inspection. Odeurs d'égout, taches après usage d'un appareil.",
    eyebrow: 'Types de fuite',
    h1: 'Recherche de fuite sur évacuations et égouts',
    image: inspectionImg,
    imageAlt: "Inspection caméra d'une canalisation d'égout fissurée depuis un regard",
    intro: [
      "Les évacuations ne sont pas sous pression : une fuite n'apparaît que lorsque l'eau s'écoule, après une douche, une lessive ou une chasse d'eau. C'est pourquoi elles échappent aux méthodes utilisées pour l'eau sous pression. Expomis dispose des techniques propres aux réseaux d'évacuation : fluorescéine, test fumigène et caméra d'inspection.",
    ],
    sections: [
      {
        h2: 'Les signes d’une fuite d’évacuation',
        list: [
          "une tache qui apparaît ou s'agrandit après l'utilisation d'un appareil précis ;",
          "des odeurs d'égout dans une pièce ou dans la cave ;",
          "de l'humidité au pied d'une descente ou le long d'un mur de cave ;",
          'un écoulement lent ou des refoulements récurrents ;',
          'un affaissement du sol au-dessus d’une canalisation enterrée.',
        ],
      },
      {
        h2: 'Trois techniques complémentaires',
        list: [
          "[Fluorescéine](/techniques/fluoresceine) : on colore l'eau d'un appareil à la fois et on observe où elle réapparaît. On sait ainsi quel appareil, quelle évacuation est en cause.",
          '[Test fumigène](/techniques/test-fumigene) : une fumée non toxique envoyée dans le réseau ressort par les raccords mal emboîtés et les fissures. Idéal pour les odeurs.',
          "[Caméra d'inspection](/techniques/camera-canalisation) : on voit l'intérieur du tuyau, fissures, joints déboîtés, racines, écrasement, contre-pente.",
        ],
      },
      {
        h2: 'Réseaux enterrés et raccordement à l’égout',
        body: [
          "Sous une dalle de cave ou dans le jardin, une canalisation d'évacuation fissurée laisse l'eau se perdre dans le sol et peut, avec le temps, déstabiliser le terrain. La caméra d'inspection permet de situer le défaut par la longueur de câble déroulée depuis le regard, et donc de ne creuser qu'au bon endroit.",
        ],
      },
    ],
    faq: [
      {
        q: "Les odeurs d'égout viennent-elles forcément d'une fuite ?",
        a: "Non. Un siphon désamorcé (appareil rarement utilisé) ou une ventilation absente peuvent laisser remonter les odeurs. Le test fumigène montre exactement par où elles entrent.",
      },
      {
        q: 'La fluorescéine est-elle dangereuse ?',
        a: "Non. C'est un colorant non toxique, utilisé en très faible concentration, qui est aussi employé pour le traçage des eaux souterraines.",
      },
    ],
    related: ['/techniques/test-fumigene', '/techniques/camera-canalisation', '/recherche-fuite-douche'],
  },
  {
    path: '/recherche-fuite-douche',
    parent: PARENT,
    nav: 'Fuite de douche ou sous carrelage',
    summary: 'Douche, douche à l’italienne, baignoire : alimentation, évacuation ou étanchéité ?',
    title: 'Fuite de douche, baignoire ou sous carrelage | Expomis',
    description: "Fuite de douche, de douche à l'italienne ou de baignoire : nous testons alimentation, évacuation et étanchéité pour trouver la vraie cause, sans casse.",
    eyebrow: 'Types de fuite',
    h1: 'Fuite de douche, de baignoire ou sous carrelage',
    image: fluoresceineImg,
    imageAlt: "Test à la fluorescéine sous lampe UV : l'eau colorée révèle une fuite d'évacuation",
    intro: [
      "Une tache au plafond sous la salle de bain, un joint qui noircit, un carrelage qui sonne creux : la douche est l'une des sources de fuite les plus fréquentes. La difficulté est de savoir d'où vient l'eau, car trois origines sont possibles et chacune appelle une réparation différente.",
    ],
    sections: [
      {
        h2: 'Trois origines possibles',
        list: [
          "L'alimentation : l'arrivée d'eau chaude ou froide du mitigeur, encastrée dans le mur. Elle fuit en permanence, même quand la douche n'est pas utilisée.",
          "L'évacuation : la bonde, le siphon, le raccord au tuyau d'évacuation. Elle ne fuit que quand l'eau s'écoule.",
          "L'étanchéité : les joints, le receveur, la natte ou la membrane sous le carrelage. L'eau passe quand elle ruisselle sur les parois ou stagne au sol.",
        ],
        after: [
          "Refaire les joints ne sert à rien si la fuite vient de l'alimentation. Remplacer le siphon ne sert à rien si c'est l'étanchéité. D'où l'intérêt de tester chaque origine séparément avant de lancer des travaux.",
        ],
      },
      {
        h2: 'Comment nous les distinguons',
        list: [
          "Alimentation : [contrôle de pression](/techniques/mise-en-pression) des arrivées, et [caméra thermique](/techniques/camera-thermique) pour suivre le tracé de l'eau chaude.",
          "Évacuation : on fait couler de l'eau colorée à la [fluorescéine](/techniques/fluoresceine) uniquement dans la bonde, sans mouiller les parois, et on observe si elle réapparaît dans la tache.",
          "Étanchéité : on arrose zone par zone (parois, angles, pied de paroi, seuil) et on suit l'évolution de l'humidité à l'[humidimètre](/techniques/humidimetre).",
        ],
      },
      {
        h2: 'Le cas des douches à l’italienne',
        body: [
          "Sans receveur, l'étanchéité d'une douche à l'italienne repose entièrement sur la membrane posée sous le carrelage. Les défauts se situent le plus souvent autour du siphon de sol, en pied de paroi et au niveau du seuil. Ce sont les zones que nous testons en priorité.",
        ],
      },
      {
        h2: 'En appartement',
        body: [
          "La fuite se voit généralement chez le voisin du dessous. Nos tests établissent si elle vient bien de votre salle de bain, et de quel élément, ce qui est indispensable pour le [dossier de dégât des eaux](/degat-des-eaux).",
        ],
      },
    ],
    faq: [
      {
        q: 'Refaire les joints suffit-il à régler une fuite de douche ?',
        a: "Seulement si l'eau passe par les joints. Si la fuite vient de l'alimentation, de l'évacuation ou de la membrane sous le carrelage, refaire les joints ne changera rien.",
      },
    ],
    related: ['/recherche-fuite-evacuation', '/techniques/fluoresceine', '/degat-des-eaux'],
  },
  {
    path: '/recherche-infiltration',
    parent: PARENT,
    nav: 'Infiltration d’eau',
    summary: "Toiture, terrasse, façade, cave : retrouver par où l'eau de pluie entre.",
    title: "Recherche d'infiltration : toiture, terrasse, façade | Expomis",
    description: "Recherche d'infiltration d'eau par la toiture, une terrasse, une façade ou en cave : mises en eau ciblées, fluorescéine, thermographie, mesure d'humidité.",
    eyebrow: 'Types de fuite',
    h1: "Recherche d'infiltration d'eau : toiture, terrasse, façade, cave",
    image: humidimetreImg,
    imageAlt: "Mesure de l'humidité d'un mur touché par une infiltration",
    intro: [
      "Une infiltration n'est pas une fuite de canalisation : l'eau vient de l'extérieur, le plus souvent la pluie, et entre par un défaut de l'enveloppe du bâtiment. Elle apparaît souvent loin de son point d'entrée, et seulement par certains temps, ce qui la rend difficile à localiser. Expomis retrace le chemin de l'eau pour trouver où elle entre.",
    ],
    sections: [
      {
        h2: 'Les signes d’une infiltration',
        list: [
          "une tache au plafond sous une toiture ou une terrasse, qui s'agrandit après la pluie ;",
          "de l'humidité sur un mur exposé aux pluies battantes, ou autour d'une fenêtre ;",
          'une cave qui prend l’eau après un orage ;',
          'des traces qui varient avec la météo plutôt qu’avec l’utilisation de l’eau dans la maison.',
        ],
      },
      {
        h2: 'Infiltration ou autre chose ?',
        body: [
          "Une tache d'humidité n'est pas forcément une infiltration. Une fuite de canalisation, la condensation ou des remontées capillaires donnent des symptômes proches. La cartographie à l'[humidimètre](/techniques/humidimetre), le lien avec la météo et un [contrôle de pression](/techniques/mise-en-pression) des canalisations voisines permettent de trancher.",
        ],
      },
      {
        h2: 'Comment nous localisons une infiltration',
        list: [
          "Mises en eau ciblées : on arrose une zone précise (un relevé d'étanchéité, une évacuation de terrasse, un seuil, un encadrement de fenêtre) et on observe si l'eau ressort à l'intérieur. Colorée à la [fluorescéine](/techniques/fluoresceine), elle se reconnaît sans ambiguïté.",
          "[Caméra thermique](/techniques/camera-thermique) : les zones humides d'un plafond ou d'un isolant apparaissent plus froides, ce qui révèle l'étendue et le cheminement de l'eau.",
          "[Endoscopie](/techniques/endoscopie) : pour voir dans un faux plafond, un doublage ou une cavité sans démonter.",
        ],
      },
      {
        h2: 'Toitures plates et terrasses',
        body: [
          "Sur une toiture plate ou une terrasse, l'eau entre rarement au milieu de la surface. Les points faibles se trouvent aux relevés contre les murs, autour des évacuations, aux joints et aux seuils de portes-fenêtres. Nous testons ces zones une par une pour isoler celle qui laisse passer l'eau.",
        ],
      },
    ],
    faq: [
      {
        q: 'Faut-il attendre qu’il pleuve pour trouver une infiltration ?',
        a: "Non. Nous reproduisons l'arrivée d'eau par des mises en eau ciblées, zone par zone, ce qui permet d'identifier le point d'entrée par temps sec.",
      },
    ],
    related: ['/techniques/humidimetre', '/techniques/fluoresceine', '/recherche-fuite-virton'],
  },
  {
    path: '/surconsommation-eau',
    parent: PARENT,
    nav: 'Surconsommation d’eau',
    summary: 'Facture anormale, compteur qui tourne : trouver la fuite qui ne laisse aucune trace.',
    title: "Surconsommation d'eau, compteur qui tourne | Expomis",
    description: "Facture d'eau anormale ou compteur qui tourne robinets fermés ? Vérifications à faire soi-même, puis recherche de la fuite cachée sans casse.",
    eyebrow: 'Types de fuite',
    h1: "Surconsommation d'eau : trouver la fuite cachée",
    intro: [
      "Une facture d'eau qui augmente fortement sans changement dans vos habitudes, un compteur qui tourne alors que tout est fermé : l'eau s'échappe quelque part, parfois sans aucune trace visible, parce qu'elle part directement dans une évacuation ou dans le sol.",
    ],
    sections: [
      {
        h2: 'Vérifier vous-même en 30 minutes',
        ordered: true,
        list: [
          'Fermez tous les robinets et arrêtez les appareils qui consomment de l’eau (lave-linge, lave-vaisselle, adoucisseur, arrosage).',
          "Relevez l'index du compteur, ou observez la petite roue ou l'étoile qui tourne dès qu'il y a un débit.",
          "Attendez 30 minutes sans utiliser d'eau, puis relevez à nouveau.",
          "Si l'index a bougé, il y a une fuite en aval du compteur.",
        ],
        after: [
          "Avant de nous appeler, contrôlez les fuites invisibles les plus courantes : une chasse d'eau qui coule en continu dans la cuvette (quelques gouttes de colorant dans le réservoir le révèlent), le groupe de sécurité du chauffe-eau qui goutte dans l'évacuation, un adoucisseur bloqué en régénération, un robinet extérieur.",
        ],
      },
      {
        h2: 'Si ces vérifications ne donnent rien',
        body: [
          "La fuite est alors probablement sur une [canalisation encastrée ou enterrée](/recherche-fuite-canalisation). Nous isolons les circuits un par un par [contrôle de pression](/techniques/mise-en-pression) pour savoir lequel perd de l'eau, puis nous localisons le point de fuite par [écoute acoustique](/techniques/detection-acoustique) ou au [gaz traceur](/techniques/gaz-traceur).",
        ],
      },
      {
        h2: 'Votre facture',
        body: [
          "Certains distributeurs ou communes accordent une réduction sur la facture lorsqu'une fuite cachée a été localisée et réparée. Renseignez-vous auprès du vôtre : notre [rapport](/rapport-recherche-fuite) documente la fuite et son emplacement.",
        ],
      },
    ],
    faq: [
      {
        q: 'Mon assurance rembourse-t-elle l’eau perdue ?',
        a: "Rarement. Les contrats couvrent en général les dommages causés par l'eau, et souvent les frais de recherche de fuite, plus rarement la consommation perdue. Vérifiez vos conditions.",
      },
    ],
    related: ['/recherche-fuite-canalisation', '/techniques/mise-en-pression', '/techniques/detection-acoustique'],
  },  {
    path: '/prix-recherche-fuite',
    parent: PARENT,
    nav: "Prix d'une recherche de fuite",
    summary: "Prix fixé à l'avance pour chaque prestation, forfait possible pour une recherche simple.",
    title: "Prix d'une recherche de fuite au Luxembourg | Expomis",
    description: "Combien coûte une recherche de fuite au Luxembourg ? Prix fixé avant chaque prestation, forfait pour une recherche simple, prise en charge par l'assurance.",
    eyebrow: 'Tarifs',
    h1: "Prix d'une recherche de fuite au Luxembourg",
    intro: [
      "Chez Expomis, une recherche de fuite est facturée à la prestation, à un prix fixé à l'avance. Vous nous décrivez la situation, nous vous expliquons comment nous procéderions et nous vous indiquons le prix avant l'intervention. Si la fuite est trouvée en une seule visite, sans démontage, un forfait s'applique lorsqu'il vous est plus favorable.",
    ],
    sections: [
      {
        h2: "Un prix fixé avant l'intervention",
        body: [
          "Le prix est convenu avec vous avant que nous commencions, sur la base de ce que vous nous décrivez. Vous savez ce que vous paierez, et vous pouvez le soumettre à votre assureur ou à votre syndic avant de donner votre accord.",
          "Nous n'affichons pas de tarif unique, parce que deux recherches de fuite ne se ressemblent pas. Une tache sous une douche dans un appartement et une perte de pression sur un chauffage au sol de 150 m² ne demandent ni le même temps ni les mêmes appareils. Un prix affiché serait trop élevé pour la première ou trompeur pour la seconde.",
        ],
      },
      {
        h2: 'Le forfait pour une recherche simple',
        body: [
          "Une recherche est simple lorsque la fuite est trouvée en une seule visite, sans démontage pour réaliser les tests. Dans ce cas, un forfait peut s'appliquer.",
          "Nous faisons le calcul après la première visite : si le forfait est plus avantageux pour vous que le prix de la prestation, c'est le forfait qui est appliqué.",
        ],
      },
      {
        h2: 'Ce qui fait varier le prix',
        list: [
          "le type de réseau : une canalisation sous pression, une [évacuation](/recherche-fuite-evacuation) ou une [infiltration](/recherche-infiltration) ne se recherchent pas de la même façon ;",
          "le nombre de circuits à contrôler : eau froide, eau chaude, chauffage, boucles d'un chauffage au sol ;",
          "la taille du bâtiment : appartement, maison, immeuble avec parties communes ;",
          "les [techniques](/techniques) nécessaires : un [gaz traceur](/techniques/gaz-traceur) ou une [inspection caméra](/techniques/camera-canalisation) demandent plus de préparation qu'une mesure d'humidité ;",
          "l'accès aux installations : regards, faux plafonds, gaines, collecteur.",
        ],
      },
      {
        h2: 'Ce qu’il nous faut pour vous donner un prix',
        list: [
          "le type de bien et l'étage, et si le logement est en copropriété ;",
          "ce que vous constatez, et depuis quand : tache, compteur qui tourne, chaudière qui perd de la pression, odeur ;",
          "quelques photos de la zone touchée, et du compteur si vous le soupçonnez ;",
          "ce qui a déjà été fait : passage d'un plombier, ouverture, déclaration à l'assurance.",
        ],
        after: [
          "Vous pouvez nous envoyer ces éléments par le [formulaire de contact](/contact) ou nous appeler.",
        ],
      },
      {
        h2: 'Qui paie la recherche de fuite ?',
        body: [
          "Au Luxembourg, de nombreux contrats d'assurance habitation prennent en charge les frais de recherche de fuite lorsque la fuite a causé un dégât des eaux, souvent jusqu'à un plafond. Vérifiez votre contrat ou demandez à votre agent. Notre [rapport](/rapport-recherche-fuite) est la pièce que l'assureur attend pour instruire le dossier.",
          "Locataire, propriétaire, copropriété, voisin : la répartition dépend de l'endroit où se trouve la fuite. Notre guide [Fuite d'eau au Luxembourg : qui paie ?](/fuite-eau-qui-paie) détaille chaque situation.",
        ],
      },
    ],
    faq: [
      {
        q: 'Combien coûte une recherche de fuite au Luxembourg ?',
        a: "Le prix dépend de la situation : type de fuite, nombre de circuits à contrôler, techniques nécessaires. Il est fixé avec vous avant l'intervention. Si la fuite est trouvée en une seule visite, sans démontage, nous appliquons un forfait lorsqu'il vous est plus favorable. Décrivez-nous la situation pour obtenir un prix.",
      },
      {
        q: 'Pourquoi ne pas afficher un prix ?',
        a: "Parce qu'il ne serait juste que pour une partie des cas. Nous préférons vous annoncer, avant l'intervention, le prix correspondant à votre situation.",
      },
      {
        q: "L'assurance rembourse-t-elle la recherche de fuite ?",
        a: "Souvent, lorsque la fuite a causé un dégât des eaux, dans la limite des plafonds de votre contrat. Le prix fixé à l'avance vous permet de le soumettre à votre assureur avant l'intervention.",
      },
    ],
    related: ['/fuite-eau-qui-paie', '/rapport-recherche-fuite', '/recherche-fuite-luxembourg'],
  },
];
